import { useEffect } from "react";
import { locales, defaultLocale, indexableOnly, isIndexableLocale, type Locale } from "@/lib/i18n/config";
import { localizePath } from "@/lib/i18n/localize-path";
import { useLocale } from "@/lib/i18n/LocaleContext";
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL, stripSiteSuffix, withSiteName } from "@/lib/site-constants";
import { pageCopyFor } from "@/lib/seo/page-copy";
import { appendManagedLink, removeManagedLinks, removeMeta, upsertJsonLd, upsertLink, upsertMeta } from "@/lib/seo/dom";

export interface HeadTagsInput {
  /** Plain page title, without the site suffix — the hook appends " | Heartopia.guide". */
  title: string;
  /** Localized meta description. */
  description: string;
  /** Unprefixed canonical path, e.g. "/fish/anglerfish" or "/". Locale prefix is applied internally. */
  path: string;
  /** OG/Twitter image URL (absolute or root-relative). Falls back to the site-wide default. */
  image?: string;
  /** og:type. Defaults to "website". */
  type?: "website" | "article";
  /** Structured data to emit as a single JSON-LD <script> block. Omit to clear any existing block. */
  jsonLd?: object | object[];
  /** Set for pages that should never be indexed (e.g. the 404 page). Defaults to false. */
  noindex?: boolean;
  /**
   * Locales this page actually has translated content for — the hreflang cluster.
   *
   * Defaults to every supported locale, which is right for pages whose copy lives in the i18n
   * dictionaries (all of them are translated by definition). Pages whose content is per-record
   * — item details, guides, news — must pass the real set, because generate-static-meta.ts only
   * writes a file and an hreflang link for those locales. Leaving the default there makes the
   * hydrated head advertise locale URLs the prerender deliberately omitted, which is the same
   * prerender-vs-hydration drift the title comment below describes.
   */
  alternateLocales?: readonly Locale[];
}

function localeToOgLocale(locale: Locale): string {
  return locale.replace("-", "_");
}

/**
 * Client-side head-tag manager for the pure-CSR app. Sets title, meta
 * description, canonical, hreflang alternates, Open Graph / Twitter tags, and
 * an optional JSON-LD block on every route. Every routed page must call this
 * (see docs/SEO.md or the rollout list in the SEO implementation plan) so the
 * incoming page's effect always overwrites the outgoing page's tags in the
 * same commit — there is no unmount cleanup by design.
 */
export function useHeadTags(input: HeadTagsInput): void {
  const { locale } = useLocale();
  const {
    title,
    description,
    path,
    image,
    type = "website",
    jsonLd,
    noindex = false,
    alternateLocales = locales,
  } = input;

  // Routes with SEO copy in page-copy.ts use it, ignoring whatever the component passed.
  //
  // That override is the point, not a convenience. The prerendered HTML is built from this same
  // table, while components were passing their on-page heading ("Catch Every Fish", "Tracker") —
  // so document.title changed a few milliseconds after hydration and, because Google renders JS,
  // the short heading is what got indexed. All 12 index routes diverged this way. Rather than edit
  // every component and rely on the two staying in sync by hand, the lookup happens here, once.
  //
  // Components not covered by the table (detail pages, which build a title from item data) keep
  // passing their own, unchanged.
  const seoCopy = pageCopyFor(path, locale);
  const finalTitle = seoCopy ? stripSiteSuffix(seoCopy.title) : title;
  const finalDescription = seoCopy ? seoCopy.description : description;

  // The single client-side choke point for index eligibility (buildHead is its prerender twin).
  //
  // Callers pass the locales a page *exists* in; INDEXABLE_LOCALES decides which of those we ask
  // Google to index. Filtering here rather than in localesForItem/Guide/News is deliberate: those
  // same functions drive locale-availability.ts#linkLocaleFor, and narrowing them would send every
  // internal link on a held-out locale's page to its English target.
  //
  // A page under a held-out locale still renders normally for the reader — it just carries
  // `noindex, follow` and lists no hreflang alternate pointing back at itself.
  // Mirrors generate-static-meta.ts#buildHead: a withheld page emits no hreflang at all, not the
  // cluster it was excluded from — those pages do not list it back, and a noindexed page has no
  // business claiming alternates.
  //
  // Gate hreflang on pageNoindex, NOT on the locale alone. Withholding has two independent causes —
  // the locale (INDEXABLE_LOCALES) and the page itself (a thin item, the 404) — and buildHead
  // suppresses hreflang for both. Keying this on the locale only made /tools/axe prerender with no
  // alternates and then hydrate four of them back in, which is precisely the drift this file and
  // alternate-locales.ts keep warning about.
  const pageNoindex = noindex || !isIndexableLocale(locale);
  const hreflangLocales = pageNoindex ? [] : indexableOnly(alternateLocales);

  useEffect(() => {
    document.documentElement.lang = locale;
    // withSiteName, not a plain template: the suffix is dropped once it would push the title past
    // what SERPs show. generate-static-meta.ts applies the identical rule to the prerendered
    // <title>, so hydration doesn't change it.
    const documentTitle = withSiteName(finalTitle);
    document.title = documentTitle;

    upsertMeta({ attr: "name", value: "description" }, finalDescription);
    if (pageNoindex) {
      upsertMeta({ attr: "name", value: "robots" }, "noindex, follow");
    } else {
      removeMeta({ attr: "name", value: "robots" });
    }

    const canonicalPath = localizePath(path, locale);
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    upsertLink("canonical", { href: canonicalUrl });

    removeManagedLinks("alternate");
    for (const altLocale of hreflangLocales) {
      appendManagedLink("alternate", {
        hreflang: altLocale,
        href: `${SITE_URL}${localizePath(path, altLocale)}`,
      });
    }
    // Matches generate-static-meta.ts#buildHead: x-default points at the English URL, so it is
    // only meaningful when English is actually part of this page's cluster.
    if (hreflangLocales.includes(defaultLocale)) {
      appendManagedLink("alternate", {
        hreflang: "x-default",
        href: `${SITE_URL}${localizePath(path, defaultLocale)}`,
      });
    }

    const ogImage = image || DEFAULT_OG_IMAGE;
    // The suffixed title, matching what generate-static-meta.ts writes into og:title — these had
    // drifted too: the prerender emitted the full title while this emitted the bare one.
    upsertMeta({ attr: "property", value: "og:title" }, documentTitle);
    upsertMeta({ attr: "property", value: "og:description" }, finalDescription);
    upsertMeta({ attr: "property", value: "og:url" }, canonicalUrl);
    upsertMeta({ attr: "property", value: "og:type" }, type);
    upsertMeta({ attr: "property", value: "og:image" }, ogImage);
    upsertMeta({ attr: "property", value: "og:site_name" }, SITE_NAME);
    upsertMeta({ attr: "property", value: "og:locale" }, localeToOgLocale(locale));

    upsertMeta({ attr: "name", value: "twitter:card" }, "summary_large_image");
    upsertMeta({ attr: "name", value: "twitter:title" }, documentTitle);
    upsertMeta({ attr: "name", value: "twitter:description" }, finalDescription);
    upsertMeta({ attr: "name", value: "twitter:image" }, ogImage);

    upsertJsonLd(jsonLd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    finalTitle,
    finalDescription,
    path,
    image,
    type,
    locale,
    pageNoindex,
    hreflangLocales.join(","),
    JSON.stringify(jsonLd),
  ]);
}
